import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Repeat, SkipForward, StopCircle } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { SlidePanel } from '../common/SlidePanel';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { useUpdateScheduleEntry } from '../../hooks/useUpdateScheduleEntry';
import { useReassignScheduleEntry } from '../../hooks/useReassignScheduleEntry';
import { lookupErrorCopy } from '../../lib/errorMap';
import type { ScheduleEntry } from '../../types';
import {
  formatTime12, getEntryColor, ENTRY_TYPE_LABELS, getUserName,
  type ScheduleUser,
} from './scheduleHelpers';

interface Props {
  entry: ScheduleEntry | null;
  users: ScheduleUser[];
  onClose: () => void;
  onUpdated?: () => void;
  onError?: (message: string) => void;
}

const STATUSES = ['scheduled', 'in_progress', 'completed', 'cancelled'];
const LABEL: React.CSSProperties = { fontSize: 13, fontWeight: 500, color: palette.g500, marginBottom: 4, display: 'block' };
const INPUT: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${palette.g200}`, fontSize: 13.5, color: palette.g800, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const };
const FIELD: React.CSSProperties = { marginBottom: 16 };

export function EditEntryPanel({ entry, users, onClose, onUpdated, onError }: Props) {
  const navigate = useNavigate();
  const updateEntry = useUpdateScheduleEntry();
  const reassignEntry = useReassignScheduleEntry();
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  useEffect(() => {
    if (entry) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- panel open lifecycle: hydrate form state from incoming entry
      setTitle(entry.title);
      setStatus(entry.status);
      setNotes(entry.notes ?? '');
      setAssignedTo(entry.assigned_to?.[0] ?? '');
    }
  }, [entry]);

  if (!entry) return null;

  const color = getEntryColor(entry);
  const typeLabel = ENTRY_TYPE_LABELS[entry.type as keyof typeof ENTRY_TYPE_LABELS] ?? entry.type;
  const assignedName = getUserName(entry.assigned_to?.[0], users);

  const handleSave = async () => {
    setLoading(true);
    const updates: Record<string, unknown> = {};
    if (title !== entry.title) updates.title = title;
    if (status !== entry.status) updates.status = status;
    if (notes !== (entry.notes ?? '')) updates.notes = notes || null;
    const newAssigned = assignedTo || undefined;
    const oldAssigned = entry.assigned_to?.[0];
    const assigneeChanged = newAssigned !== oldAssigned;

    let succeeded = true;

    if (Object.keys(updates).length > 0) {
      const res = await updateEntry({ entryId: entry.id, updates });
      if (!res.success) {
        succeeded = false;
        onError?.(lookupErrorCopy('update_schedule_entry', res.error.code));
      }
    }

    if (succeeded && assigneeChanged && newAssigned) {
      const res = await reassignEntry({
        entryId: entry.id,
        newAssigneeId: newAssigned,
        idempotencyKey: crypto.randomUUID(),
      });
      if (!res.success) {
        succeeded = false;
        onError?.(lookupErrorCopy('reassign_schedule_entry', res.error.code));
      }
    } else if (succeeded && assigneeChanged && !newAssigned) {
      // Unassigning is an update_schedule_entry op (assigned_to → null), not a reassign.
      const res = await updateEntry({ entryId: entry.id, updates: { assigned_to: null } });
      if (!res.success) {
        succeeded = false;
        onError?.(lookupErrorCopy('update_schedule_entry', res.error.code));
      }
    }

    setLoading(false);
    if (succeeded) {
      onUpdated?.();
      onClose();
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    const res = await updateEntry({
      entryId: entry.id,
      updates: { status: 'cancelled' },
    });
    setLoading(false);
    setConfirmCancel(false);
    if (res.success) {
      onUpdated?.();
      onClose();
    } else {
      onError?.(lookupErrorCopy('update_schedule_entry', res.error.code));
    }
  };

  const handleSkipInstance = async () => {
    setLoading(true);
    const res = await updateEntry({
      entryId: entry.id,
      updates: { is_skipped: true, status: 'cancelled' },
    });
    setLoading(false);
    if (res.success) {
      onUpdated?.();
      onClose();
    } else {
      onError?.(lookupErrorCopy('update_schedule_entry', res.error.code));
    }
  };

  const handleStopSeries = () => {
    // Series-level mutations need a server-side RPC that operates by series_id
    // (update parent recurrence_end + cancel future instances atomically). Queued
    // as §Schedule.StopSeriesNeedsServerRPC; surface a clear "not yet" message
    // rather than silently failing.
    onError?.('Stopping a recurring series isn\'t available yet. Cancel each instance individually for now.');
  };

  return (
    <SlidePanel isOpen={!!entry} onClose={onClose} title={`Edit ${typeLabel}`} subtitle={entry.title}>
      {/* Type badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
        <span style={{
          padding: '3px 10px', borderRadius: 4, fontSize: 11.5, fontWeight: 600,
          background: color + '20', color,
        }}>{typeLabel}</span>
        <span style={{ fontSize: 12, color: palette.g400 }}>
          {entry.all_day ? 'All day' : `${formatTime12(entry.start_at)} – ${formatTime12(entry.end_at)}`}
        </span>
        {assignedName !== 'Unassigned' && (
          <span style={{ fontSize: 12, color: palette.g500 }}>· {assignedName}</span>
        )}
      </div>

      {/* Source entity link */}
      {entry.job_id && (
        <button onClick={() => { onClose(); void navigate(`/jobs`); }} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', marginBottom: 16,
          borderRadius: 6, border: `1px solid ${palette.g200}`, background: palette.g50,
          fontSize: 12, fontWeight: 500, color: palette.accent, cursor: 'pointer', fontFamily: 'inherit',
        }}><ExternalLink size={12} /> View Job</button>
      )}
      {entry.booking_id && !entry.job_id && (
        <button onClick={() => { onClose(); void navigate(`/bookings`); }} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', marginBottom: 16,
          borderRadius: 6, border: `1px solid ${palette.g200}`, background: palette.g50,
          fontSize: 12, fontWeight: 500, color: palette.accent, cursor: 'pointer', fontFamily: 'inherit',
        }}><ExternalLink size={12} /> View Booking</button>
      )}
      {entry.project_id && !entry.job_id && !entry.booking_id && (
        <button onClick={() => { onClose(); void navigate(`/projects`); }} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', marginBottom: 16,
          borderRadius: 6, border: `1px solid ${palette.g200}`, background: palette.g50,
          fontSize: 12, fontWeight: 500, color: palette.accent, cursor: 'pointer', fontFamily: 'inherit',
        }}><ExternalLink size={12} /> View Project</button>
      )}

      {/* Recurring series actions */}
      {entry.series_id && entry.recurrence_parent_id && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, background: palette.accentWash, fontSize: 11, fontWeight: 600, color: palette.accent }}>
            <Repeat size={12} /> Recurring
          </div>
          <button onClick={handleSkipInstance} disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 11, fontWeight: 500, color: palette.g600, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
            <SkipForward size={12} /> Skip this one
          </button>
          {entry.series_id && (
            <button onClick={handleStopSeries} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, border: `1px solid ${palette.red}`, background: palette.white, fontSize: 11, fontWeight: 500, color: palette.red, cursor: 'pointer', fontFamily: 'inherit' }}>
              <StopCircle size={12} /> Stop series
            </button>
          )}
        </div>
      )}

      {/* Form */}
      <div style={FIELD}>
        <label style={LABEL}>Title</label>
        <input style={INPUT} value={title} onChange={e => setTitle(e.target.value)} />
      </div>

      <div style={FIELD}>
        <label style={LABEL}>Status</label>
        <select style={INPUT} value={status} onChange={e => setStatus(e.target.value)}>
          {STATUSES.map(s => (
            <option key={s} value={s}>{s.replace('_', ' ').replace(/^\w/, c => c.toUpperCase())}</option>
          ))}
        </select>
      </div>

      <div style={FIELD}>
        <label style={LABEL}>Assigned to</label>
        <select style={INPUT} value={assignedTo} onChange={e => setAssignedTo(e.target.value)}>
          <option value="">Unassigned</option>
          {users.map(u => (
            <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
          ))}
        </select>
      </div>

      <div style={FIELD}>
        <label style={LABEL}>Notes</label>
        <textarea style={{ ...INPUT, minHeight: 60, resize: 'vertical' }} value={notes} onChange={e => setNotes(e.target.value)} />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', paddingTop: 16, borderTop: `1px solid ${palette.g100}` }}>
        <button onClick={() => setConfirmCancel(true)} style={{
          padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.red}`,
          background: palette.white, fontSize: 13, fontWeight: 500, color: palette.red,
          cursor: 'pointer', fontFamily: 'inherit',
        }}>Cancel Entry</button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{
            padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`,
            background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600,
            cursor: 'pointer', fontFamily: 'inherit',
          }}>Close</button>
          <button onClick={handleSave} disabled={loading} style={{
            padding: '8px 22px', borderRadius: 8, border: 'none',
            background: loading ? palette.g300 : palette.accent, color: palette.white,
            fontSize: 13.5, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
          }}>{loading ? 'Saving...' : 'Save'}</button>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmCancel}
        title={`Cancel this ${typeLabel.toLowerCase()}?`}
        message="This will mark the entry as cancelled. It won't be deleted."
        confirmLabel="Cancel Entry"
        onConfirm={handleCancel}
        onClose={() => setConfirmCancel(false)}
      />
    </SlidePanel>
  );
}
